import { AnimatePresence, motion } from 'framer-motion'
import { FC } from 'react'

import { RichIcon, type RichIconProps } from '@ui/components/RichIcon'
import { Color, SurfaceColorType, TextColorType } from '@ui/design-system/colors/colors'
import { StatusCancelledIcon } from '@ui/icons/StatusCancelled.icon'
import { StatusCompletedIcon } from '@ui/icons/StatusCompleted.icon'
import { StatusCustomIcon } from '@ui/icons/StatusCustom.icon'
import { StatusPendingIcon } from '@ui/icons/StatusPending.icon'
import { StatusReversedIcon } from '@ui/icons/StatusReversed.icon'
import { StatusReviewingIcon } from '@ui/icons/StatusReviewing.icon'
import { TimeIcon } from '@ui/icons/Time.icon'
import { IconFCType } from '@ui/icons/types'

import { ExportHubReportStatusId } from '@model/ExportHub'
import { RebateApprovalStatus } from '@model/RebateApprovalsDto'
import { TransactionStatus } from '@model/TransactionStatus'

export interface StatusStyle {
  icon: IconFCType
  bg: SurfaceColorType
  color: TextColorType
}

export type StatusStyleMap<K extends PropertyKey> = Record<K, StatusStyle>

export const TRANSACTION_STATUS_STYLE: StatusStyleMap<TransactionStatus> = {
  [TransactionStatus.Completed]: {
    icon: StatusCompletedIcon,
    bg: Color.Surface.SuccessBaseEm,
    color: Color.Text.SuccessHighEm,
  },
  [TransactionStatus.AwaitingApproval]: {
    icon: StatusReviewingIcon,
    bg: Color.Surface.InfoPBaseEm,
    color: Color.Text.PurpleHighEm,
  },
  [TransactionStatus.Pending]: {
    icon: StatusPendingIcon,
    bg: Color.Surface.InfoPBaseEm,
    color: Color.Text.PurpleHighEm,
  },
  [TransactionStatus.Cancelled]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.WarningLowEm,
    color: Color.Text.WarningHighEm,
  },
  [TransactionStatus.Declined]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DangerBaseEm,
    color: Color.Text.DangerHighEm,
  },
  [TransactionStatus.Failed]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DangerBaseEm,
    color: Color.Text.DangerHighEm,
  },
  [TransactionStatus.Reversed]: {
    icon: StatusReversedIcon,
    bg: Color.Surface.Surface2,
    color: Color.Text.LowEm,
  },
  [TransactionStatus.None]: {
    icon: StatusCustomIcon,
    bg: Color.Surface.Surface2,
    color: Color.Text.LowEm,
  },
}
export const APPROVAL_STATUS_STYLE: StatusStyleMap<RebateApprovalStatus> = {
  [RebateApprovalStatus.New]: {
    icon: StatusCustomIcon,
    bg: Color.Surface.Surface2,
    color: Color.Text.LowEm,
  },

  [RebateApprovalStatus.Pending]: {
    icon: StatusPendingIcon,
    bg: Color.Surface.InfoPBaseEm,
    color: Color.Text.PurpleHighEm,
  },

  [RebateApprovalStatus.Approved]: {
    icon: StatusCompletedIcon,
    bg: Color.Surface.SuccessBaseEm,
    color: Color.Text.SuccessHighEm,
  },

  [RebateApprovalStatus.Rejected]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DangerBaseEm,
    color: Color.Text.DangerHighEm,
  },

  [RebateApprovalStatus.Failed]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DangerBaseEm,
    color: Color.Text.DangerHighEm,
  },

  [RebateApprovalStatus.Reversed]: {
    icon: StatusReversedIcon,
    bg: Color.Surface.Surface2,
    color: Color.Text.LowEm,
  },
}
export const EXPORT_HUB_STATUS_STYLE: StatusStyleMap<ExportHubReportStatusId> = {
  [ExportHubReportStatusId.Processing]: {
    icon: TimeIcon,
    bg: Color.Surface.InfoPBaseEmAlpha,
    color: Color.Text.PurpleHighEm,
  },

  [ExportHubReportStatusId.Ready]: {
    icon: StatusCompletedIcon,
    bg: Color.Surface.SuccessBaseEm,
    color: Color.Text.SuccessHighEm,
  },

  [ExportHubReportStatusId.Failed]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DangerLowEmAlpha,
    color: Color.Text.DangerHighEm,
  },

  [ExportHubReportStatusId.Expired]: {
    icon: StatusCancelledIcon,
    bg: Color.Surface.DisabledLowEm,
    color: Color.Text.LowEm,
  },
}

type StatusIconProps<K extends PropertyKey> = {
  status: K | null | undefined
  styles: StatusStyleMap<K>
  fallbackStatus?: K
  animated?: boolean
  size?: RichIconProps['size']
}

export function StatusIcon<K extends PropertyKey>({
  status,
  styles,
  fallbackStatus,
  animated = false,
  size = 'x32',
}: StatusIconProps<K>) {
  const statusStyle = status === null || status === undefined ? undefined : styles[status]
  const fallbackStyle = fallbackStatus === undefined ? undefined : styles[fallbackStatus]
  const style = statusStyle ?? fallbackStyle

  if (!style) return null

  const icon = (
    <RichIcon
      icon={{ icon: style.icon, color: style.color }}
      shape='circle'
      size={size}
      bg={style.bg}
    />
  )

  if (!animated) return icon

  return (
    <AnimatePresence initial={false} mode='wait'>
      <motion.div
        key={String(status ?? fallbackStatus ?? 'unknown')}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        style={{ display: 'flex', flexShrink: 0 }}
      >
        {icon}
      </motion.div>
    </AnimatePresence>
  )
}

export const TransactionStatusIcon: FC<{
  status: TransactionStatus | null | undefined
  animated?: boolean
  size?: RichIconProps['size']
}> = ({ status, animated = true, size }) => (
  <StatusIcon
    status={status}
    styles={TRANSACTION_STATUS_STYLE}
    fallbackStatus={TransactionStatus.None}
    animated={animated}
    size={size}
  />
)

export const ExportHubStatusIcon: FC<{
  status: ExportHubReportStatusId | null | undefined
  animated?: boolean
  size?: RichIconProps['size']
}> = ({ status, animated = true, size }) => (
  <StatusIcon
    status={status}
    styles={EXPORT_HUB_STATUS_STYLE}
    fallbackStatus={ExportHubReportStatusId.Processing}
    animated={animated}
    size={size}
  />
)
